import { Region } from "@/api/studio/data/types/region.type";
import { SceneMetric } from "../../stores/use-reports-scene-store";
import { Progress } from "@/components/ui/progress";
import { getPowConfig } from "../../utils/get-pow-config";
import { getMetricColorValue } from "../../utils/get-metric-color-value";
import { getSceneMetricValue, formatSceneMetricValue } from "../../utils/scene-metric-utils";

/* Prop Types */
interface SceneRegionTableProps {
  regions: Region[];
  metric: SceneMetric;
}

/* POW Indicator component */
const PowIndicator = ({ value }: { value: number }) => {
  const { bg, icon: Icon } = getPowConfig(value);
  return <Icon className="size-4" style={{ color: bg }} />;
};

/*
 * SceneRegionTable component
 *
 * Displays a table of regions for the selected scene metric (vis, pop, soa, pow).
 * Sorts regions by metric value in descending order.
 */
export const SceneRegionTable = ({ regions, metric }: SceneRegionTableProps) => {
  /* Sort regions by metric value (descending) */
  const sortedRegions = [...regions].sort((a, b) => getSceneMetricValue(b, metric) - getSceneMetricValue(a, metric));

  /* Get metric display name */
  const getMetricLabel = () => {
    switch (metric) {
      case "vis":
        return "Visibility";
      case "pop":
        return "POP";
      case "soa":
        return "SOA";
      case "pow":
        return "POW";
      default:
        return "Visibility";
    }
  };

  /* Render */
  return (
    <div className="flex flex-col p-4">
      {/* Header */}
      {metric === "pow" ? (
        <div className="rounded-t-lg bg-base-200 px-4 py-3 grid grid-cols-[16px_1fr_100px] gap-4 border-b border-base-300 items-center">
          <p className="text-xs opacity-60 font-bold col-span-2">AOI</p>
          <p className="text-xs opacity-60 font-bold text-right">POW</p>
        </div>
      ) : (
        <div className="rounded-t-lg bg-base-200 px-4 py-3 grid grid-cols-[16px_1fr_48px_160px] gap-4 border-b border-base-300 items-center">
          <p className="text-xs opacity-60 font-bold col-span-2">AOI</p>
          <p className="text-xs opacity-60 font-bold text-center">Gaze</p>
          <p className="text-xs opacity-60 font-bold text-right">{getMetricLabel()}</p>
        </div>
      )}

      {/* Region List */}
      <div className="flex flex-col">
        {sortedRegions.map((region) => {
          const metricValue = getSceneMetricValue(region, metric);
          const progressValue = getMetricColorValue(metricValue, metric);
          const displayValue = formatSceneMetricValue(metricValue, metric);
          const isPow = metric === "pow";

          if (isPow) {
            return (
              <div key={region.id} className="grid grid-cols-[16px_1fr_100px] gap-4 items-center py-3 px-4 border-b border-base-300 last:border-b-0">
                <div className="size-2.5 rounded-xs" style={{ backgroundColor: region.color || "#F2318A" }} />
                <div className="flex items-center min-w-0">
                  <span className="text-sm font-semibold truncate">{region.label}</span>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <PowIndicator value={metricValue} />
                  <span className="text-sm font-medium min-w-[40px] text-right">{displayValue}</span>
                </div>
              </div>
            );
          }

          return (
            <div key={region.id} className="grid grid-cols-[16px_1fr_48px_160px] gap-4 items-center py-3 px-4 border-b border-base-300 last:border-b-0">
              <div className="size-2.5 rounded-xs" style={{ backgroundColor: region.color || "#F2318A" }} />
              <div className="flex items-center min-w-0">
                <span className="text-sm font-semibold truncate">{region.label}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-semibold">{region.rank}</span>
              </div>
              <div className="flex items-center justify-end gap-3">
                <Progress value={progressValue} className="w-15" />
                <span className="text-sm font-medium min-w-[40px] text-right">{displayValue}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
